import MarkerCameraIcon from '@assets/jsx-icon/MarkerCameraIcon';
import MarkerLocationIcon from '@assets/jsx-icon/MarkerLocationIcon';
import BaseIcon from '@components/BaseIcon';
import { CameraIssues } from '@EcamModel/controllers/ILocationHttpController';
import { Camera, Location, LocationSMStatus, PurposeType, Status } from '@EcamModel/model';
import { Chip, Stack, Typography } from '@mui/material';
import { LocationStatusReadableMapping } from '@pages/cameras/details/components/CameraInformation';
import { BaseSelect } from '@pages/cameras/list';
import { cameraPriorityReadableMapping, purposesReadableMapping } from '@pages/cameras/list/components/CameraItem';
import { PriorityColor } from '@pages/locations/components/LocationItems';
import { BaseSections, Coordinate } from '@pages/maps/configs';
import { Annotation } from '@pages/maps/WrapperGoogleMaps';
import color from '@theme/Colors';
import theme from '@theme/Theme';
import _ from 'lodash';
import moment from 'moment';
import { CSSProperties } from 'react';
import { IoEyeOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { formatUKDate } from 'src/helpers';

export type SelectedMarker = {
    type?: 'location' | 'camera';
    selectedItem?: Location | Camera;
    positions?: Coordinate;
    isOpen: boolean;
};

type AnnotationsParams = {
    width: CSSProperties['width'];
    height: CSSProperties['height'];
};

export type FilterMapOverview = {
    location?: Location;
    camera?: Camera;
    searchType?: BaseSelect;
    from?: Date;
    to?: Date;
    priorityType?: BaseSelect;
    priorityLocation?: BaseSelect;
    priorityCamera?: BaseSelect;
    issueCamera?: BaseSelect;
};

const NA_TEXT = 'N/A';

export enum MapSelectType {
    Location,
    Camera,
}

export const mapSelectTypes: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Location',
        Value: MapSelectType.Location,
    },
    {
        Id: 2,
        Name: 'Camera',
        Value: MapSelectType.Camera,
    },
];

export const camerasIssues: BaseSelect[] = [
    {
        Id: 1,
        Name: 'Out of battery',
        Value: CameraIssues.OutOfBattery,
    },
    {
        Id: 2,
        Name: 'SD card error',
        Value: CameraIssues.SdCardError,
    },
    {
        Id: 3,
        Name: 'Incorrect angle',
        Value: CameraIssues.IncorrectAngle,
    },
];

export const getStatusLocationColor = (status: LocationSMStatus) => {
    switch (status) {
        case LocationSMStatus.Prospective:
            return { colorText: color.secondary, bgColor: '#E5F2FF' };
        case LocationSMStatus.Live:
            return { colorText: color.success, bgColor: '#E5F5ED' };
        case LocationSMStatus.Transferred:
            return { colorText: '#CC0066', bgColor: '#CC00661A' };
        case LocationSMStatus.Decommissioned:
            return { colorText: color.danger, bgColor: '#FCE8E5' };
        case LocationSMStatus.OnHold:
            return { colorText: '#FFB600', bgColor: '#FFF8E5' };
        default:
            return { colorText: color.danger, bgColor: '#FCE8E5' };
    }
};

export const getStatusCameraColor = (status: PurposeType) => {
    switch (status) {
        case PurposeType.Prospective:
            return { colorText: color.secondary, bgColor: '#E5F2FF' };
        case PurposeType.Live:
            return { colorText: color.success, bgColor: '#E5F5ED' };
        case PurposeType.Testing:
            return { colorText: '#CC0066', bgColor: '#CC00661A' };
        case PurposeType.Decommissioned:
            return { colorText: color.danger, bgColor: '#FCE8E5' };
        case PurposeType.OnHold:
            return { colorText: '#FFB600', bgColor: '#FFF8E5' };
        default:
            return { colorText: color.danger, bgColor: '#FCE8E5' };
    }
};

export const priorityColors: Record<number, PriorityColor> = {
    0: {
        key: 0,
        bgColorPriority: '#D4EDDA',
    },
    1: {
        key: 1,
        bgColorPriority: '#F8D7DA',
    },
    2: {
        key: 2,
        bgColorPriority: '#FFF3CD',
    },
    3: {
        key: 3,
        bgColorPriority: '#CCE5FF',
    },
    4: {
        key: 4,
        bgColorPriority: '#E2E3E5',
    },
    5: {
        key: 5,
        bgColorPriority: '#E5F2FF',
    },
};

export const getPriorityLocationsColor = (status: number) => {
    return (
        priorityColors[status] ?? {
            key: 0,
            bgColorPriority: '#FCE8E5',
        }
    );
};

export const getAnnotations = ({ width, height }: AnnotationsParams): Annotation[] => {
    return [
        {
            icon: <MarkerCameraIcon width={width} height={height} />,
            title: `The coordinates of the center of the camera`,
        },
        {
            icon: <MarkerLocationIcon fill={'#E01B00'} width={width} height={height} />,
            title: `The coordinates of the center of the designated location`,
        },
        {
            icon: <MarkerLocationIcon fill={'#FF8C00'} width={width} height={height} />,
            title: 'The coordinates of the center locations with cameras',
        },
    ];
};

export const getIconLocation = (isSelectedMarker: boolean): string =>
    isSelectedMarker ? '/assets/images/markerLocationSelected.svg' : `/assets/images/markerLocation.svg`;

export const getIconCamera = (status: Status): string => {
    const statusMap: Record<Status, string> = {
        [Status.Active]: 'Active',
        [Status.Inactive]: 'Inactive',
        [Status.NotAvailable]: 'NotAvailable',
        [Status.Deleted]: 'NotAvailable',
    };

    const suffix = statusMap[status] ?? 'Active';
    return `/assets/images/markerCamera${suffix}.svg`;
};

export const LocationInformation = (location: Location): BaseSections[] => {
    const camraOfLocation = location?.Zones?.reduce((total, zone) => {
        const validCameras =
            zone.Cameras?.filter(
                (camera) =>
                    camera.Solar4gCamera?.Solar4gDeviceStatus?.Latitude &&
                    camera.Solar4gCamera?.Solar4gDeviceStatus?.Longitude
            ) || [];

        return total + validCameras.length;
    }, 0);
    const addresses = [location?.Address1, location?.Address2, location?.Address3].filter(Boolean).join(', ');
    const pcn = location?.Zones?.reduce(
        (totals, zone) => {
            zone.Cameras?.forEach((camera) => {
                totals[0] += camera.PcnCurrentMonth ?? 0;
                totals[1] += camera.PcnLastMonthAgo ?? 0;
                totals[2] += camera.PcnThreeMonthsAgo ?? 0;
            });
            return totals;
        },
        [0, 0, 0]
    );
    const { colorText, bgColor } = getStatusLocationColor(location.LocationStatus ?? 3);
    const { bgColorPriority } = getPriorityLocationsColor(location?.Priority ?? 0);

    const handleClickNavigateCameraScreen = (locationId: number) => {
        if (locationId) {
            const taskScreenUrl = `/cameras?locationId=${locationId}&status=2&cameraType=0`;
            window.open(taskScreenUrl, '_blank');
        }
    };

    return [
        {
            label: (
                <Stack direction={'row'} spacing={1} alignItems={'center'} mr={2}>
                    {location?.Priority !== null && (
                        <Typography
                            padding={'4px 8px'}
                            variant="body2"
                            borderRadius={'5px'}
                            sx={{
                                cursor: 'default',
                                color: color.textSecondary,
                                bgcolor: bgColorPriority,
                                fontSize: 12,
                            }}
                        >
                            {`P${location?.Priority}`}
                        </Typography>
                    )}
                    <Typography
                        variant="h5"
                        onClick={() => (camraOfLocation ? handleClickNavigateCameraScreen(location.Id!) : undefined)}
                        sx={
                            camraOfLocation
                                ? { cursor: 'pointer', '&:hover': { color: color.priText }, textDecoration: 'none' }
                                : {}
                        }
                    >
                        {location.Name}
                    </Typography>

                    {!_.isNil(location.LocationStatus) && (
                        <Typography
                            color={colorText}
                            sx={{
                                padding: '4px 8px',
                                fontSize: 12,
                                background: bgColor,
                                borderRadius: '5px',
                            }}
                        >
                            {LocationStatusReadableMapping[location.LocationStatus!] ?? NA_TEXT}
                        </Typography>
                    )}
                </Stack>
            ),
            value: camraOfLocation ? (
                <BaseIcon
                    title="View details"
                    colorHover={theme.palette.secondary.main}
                    children={<IoEyeOutline size={14} />}
                    onClick={() => handleClickNavigateCameraScreen(location.Id!)}
                />
            ) : (
                <></>
            ),
        },
        {
            label: 'Camera',
            value: <Typography>{camraOfLocation}</Typography>,
        },
        {
            label: 'Address',
            value: <Typography color={color.grey600}>{addresses}</Typography>,
        },
        {
            label: 'Approved PCN',
            value: <ApprovedPCN values={pcn} />,
        },
    ];
};

export const CameraInformation = (camera: Camera): BaseSections[] => {
    const { colorText, bgColor } = getStatusCameraColor(camera.PurposeType ?? 3);

    return [
        {
            label: (
                <Stack direction={'row'} spacing={1} alignItems={'center'} mr={2}>
                    <Typography
                        variant="h5"
                        component={Link}
                        to={`/cameras/${camera.Id}`}
                        target="_blank"
                        sx={{ cursor: 'pointer', '&:hover': { color: color.priText }, textDecoration: 'none' }}
                    >
                        {camera?.Name}
                    </Typography>

                    {camera.PurposeType !== null && (
                        <Chip
                            size="small"
                            sx={{
                                borderRadius: '16px',
                                fontSize: 12,
                                px: '5px',
                                background: bgColor,
                                color: colorText,
                            }}
                            label={purposesReadableMapping[camera.PurposeType!]}
                        />
                    )}
                </Stack>
            ),
            value: (
                <Link to={`/cameras/${camera.Id}`} target="_blank">
                    <BaseIcon
                        title="View details"
                        colorHover={theme.palette.secondary.main}
                        children={<IoEyeOutline size={14} />}
                    />
                </Link>
            ),
        },
        { label: 'MAC', value: <Typography>{camera?.Solar4gCamera?.Mac}</Typography> },
        {
            label: 'Priority',
            value: (
                <Typography variant="body1" color={'#85858A'}>
                    {camera.Priority !== null ? cameraPriorityReadableMapping[camera.Priority!] : 'None'}
                </Typography>
            ),
        },
        {
            label: 'Type',
            value: (
                <Typography color={color.secondary}>
                    {camera.VendorCameraId
                        ? 'Camera Solar'
                        : camera.PTZCameraId
                        ? 'View360'
                        : camera.SP111CameraId
                        ? 'SP111'
                        : ''}
                </Typography>
            ),
        },
        {
            label: 'Production',
            value: <Typography>{formatUKDate(camera?.DeployedDate)}</Typography>,
        },
        {
            label: 'Current issues',
            value: <Typography>{camera?.CameraTasks?.length ?? 0}</Typography>,
        },
        {
            label: 'Approved PCN',
            value: (
                <ApprovedPCN
                    values={[
                        camera?.PcnCurrentMonth ?? 0,
                        camera?.PcnLastMonthAgo ?? 0,
                        camera?.PcnThreeMonthsAgo ?? 0,
                    ]}
                />
            ),
        },
        {
            label: 'Last captured image',
            value: (
                <Link to={`/cameras/${camera.Id}`} state={{ tabValue: 1 }}>
                    <BaseIcon
                        title="View"
                        colorHover={theme.palette.secondary.main}
                        children={<IoEyeOutline size={14} />}
                    />
                </Link>
            ),
        },
    ];
};

export const checkZoom = (map: google.maps.Map | null) => {
    return map?.getZoom() ? map.getZoom()! < 15 : false;
};

const ApprovedPCN = ({ values = [0, 0, 0] }) => {
    const recentMonths = [...Array(3)].map((_, i) => ({
        label: moment().subtract(i, 'months').format('MMM'),
        value: values[i] || 0,
    }));

    return (
        <Typography>
            {recentMonths.map((month, index) => (
                <span key={index}>
                    {month.label}: {month.value} {index < 2 ? ' | ' : ''}
                </span>
            ))}
        </Typography>
    );
};

export const createCirclePoints = (center: Coordinate, radius: number, numPoints: number = 360) => {
    const earthRadius = 6378137;
    const points: Coordinate[] = [];

    for (let i = 0; i < numPoints; i++) {
        const angle = (i * 360) / numPoints;
        const lat = center.lat * (Math.PI / 180);
        const lng = center.lng * (Math.PI / 180);
        const bearing = angle * (Math.PI / 180);

        const newLat = Math.asin(
            Math.sin(lat) * Math.cos(radius / earthRadius) +
                Math.cos(lat) * Math.sin(radius / earthRadius) * Math.cos(bearing)
        );
        const newLng =
            lng +
            Math.atan2(
                Math.sin(bearing) * Math.sin(radius / earthRadius) * Math.cos(lat),
                Math.cos(radius / earthRadius) - Math.sin(lat) * Math.sin(newLat)
            );

        points.push({ lat: newLat * (180 / Math.PI), lng: newLng * (180 / Math.PI) });
    }

    return points;
};
